import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Check, Loader2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * WaitlistForm — calm, single-input email capture for the Landing page.
 * - Validates client-side then POSTs to /api/waitlist.
 * - Shows an inline success state (idempotent on duplicate joins).
 */
export const WaitlistForm = ({ source = 'landing_page' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [message, setMessage] = useState('');
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [count, setCount] = useState(null);

  // Lightweight social-proof: fetch the live waitlist count once on mount.
  // Only render the line once we have a non-zero number (avoids "0 people…").
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/waitlist/count`)
      .then((res) => {
        if (!cancelled) setCount(Number(res?.data?.count) || 0);
      })
      .catch(() => {
        // Silent — social proof is a nice-to-have, never block the form.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }
    setStatus('submitting');
    setMessage('');
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: trimmed,
        source,
        referrer: typeof window !== 'undefined' ? window.location.href : '',
      });
      const data = res.data || {};
      setAlreadyJoined(Boolean(data.already_joined));
      setMessage(data.message || "You're on the list.");
      setStatus('success');
      // Optimistically reflect the new join in the count for any subsequent reveal.
      if (!data.already_joined && typeof count === 'number') {
        setCount(count + 1);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setStatus('error');
      setMessage(detail);
    }
  };

  if (status === 'success') {
    return (
      <div
        className="border border-emerald-200 bg-emerald-50 rounded-xl p-4 sm:p-5 flex items-start gap-3"
        data-testid="waitlist-success"
      >
        <div className="h-9 w-9 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
          <Check className="h-5 w-5 text-white" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-emerald-900">
            {alreadyJoined ? "You're already on the list." : "You're on the list."}
          </p>
          <p className="text-sm text-emerald-800/80 leading-relaxed">{message}</p>
          {typeof count === 'number' && count > 0 && (
            <p
              className="text-xs text-emerald-800/70 mt-1 inline-flex items-center gap-1.5"
              data-testid="waitlist-success-count"
            >
              <Users className="h-3.5 w-3.5" />
              <span>
                Joining{' '}
                <strong className="font-semibold">{count.toLocaleString()}</strong>{' '}
                {count === 1 ? 'person' : 'people'} already in line.
              </span>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-2"
      data-testid="waitlist-form"
      noValidate
    >
      <p className="text-sm font-semibold text-gray-900">
        Not ready to sign in? Join the waitlist.
      </p>
      <p className="text-sm text-gray-600 leading-relaxed mb-3">
        Get a single, calm email when HealthLoop is ready for you.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === 'error') setStatus('idle');
            }}
            disabled={status === 'submitting'}
            className="pl-9 h-12 text-base rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
            data-testid="waitlist-email-input"
            aria-label="Email address"
          />
        </div>
        <Button
          type="submit"
          disabled={status === 'submitting'}
          className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl"
          data-testid="waitlist-submit-btn"
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Joining…
            </>
          ) : (
            'Join waitlist'
          )}
        </Button>
      </div>
      {status === 'error' && (
        <p className="text-sm text-red-600 mt-1" data-testid="waitlist-error">
          {message}
        </p>
      )}
      <p className="text-xs text-gray-500 mt-2">
        No spam. One email when we're ready. Unsubscribe any time.
      </p>
      {typeof count === 'number' && count > 0 && (
        <p
          className="text-xs text-gray-500 mt-3 inline-flex items-center gap-1.5"
          data-testid="waitlist-count"
        >
          <Users className="h-3.5 w-3.5 text-emerald-600" />
          <span>
            <strong className="text-gray-900 font-semibold">
              {count.toLocaleString()}
            </strong>{' '}
            {count === 1 ? 'person is' : 'people are'} already on the list.
          </span>
        </p>
      )}
    </form>
  );
};

export default WaitlistForm;
